import { useCallback } from 'react'
import { search } from 'jmespath'
import { successNotification } from '@sceneio/cms-notifications'
import { CallbacksType } from '../types/callbacksTypes'
import { useInternalGraphql } from '../useInternalGraphql'
import { useCustomNotifications } from '../notifications/useCustomNotifications'
import { UI } from '@sceneio/content-store/lib/constants'
import { GqlErrorsType } from '../types/gqlErrorsTypes'
import { useProjectDispatch } from '@sceneio/content-store/lib/features/project/projectSlice'
import { posthog } from 'posthog-js'

type Config = {
  name: string
  workspaceId: string
  languageId?: string
  preferences?: Record<string, any>
  createInfra?: boolean
}
export function useProjectCreate(): (
  data: Config,
  callbacks?: CallbacksType,
) => Promise<void> {
  const { sendRequest: createProject } = useInternalGraphql({
    query: 'ProjectCreateMutationDocument',
    runOnInit: false,
  })
  const { loadingNotification } = useCustomNotifications()
  const projectDispatch = useProjectDispatch()

  return useCallback(
    async (
      { name, workspaceId, languageId, preferences, createInfra }: Config,
      { onSuccess, onError }: CallbacksType = {},
    ) => {
      const saveData = {
        name,
        workspaceId,
        languageId,
        preferences,
        createInfra,
      }
      projectDispatch({ type: UI.SET_LOADING_START })

      posthog.capture('project_create', {
        name,
        workspaceId,
      })

      loadingNotification.notify('createProject', 'Creating project...')
      try {
        const { data: gqlData, errors: gqlErrors } =
          (await createProject({
            variables: saveData,
            authContext: {
              workspaceId,
            },
          })) || {}

        if (gqlErrors && gqlErrors.length > 0) {
          throw gqlErrors
        }
        const responseData = search(gqlData, 'createProject')

        if (responseData) {
          projectDispatch({ type: UI.SET_LOADING_STOP })
        }
        loadingNotification.update('createProject', {
          status: 'success',
          content: `Project "${name}" created.`,
          allowUndo: false,
        })
        if (onSuccess) onSuccess(responseData)
      } catch (errs) {
        console.warn('[createProject]', errs)
        loadingNotification.update('createProject', {
          status: 'error',
          content: 'Failed to create project.',
        })
        projectDispatch({ type: UI.SET_LOADING_STOP })
        if (onError) onError(errs as GqlErrorsType)
      }
    },
    [],
  )
}
